/**
 * Icon API for wob-explorer-2.
 *
 * This is the public API consumed by all components (TreeView, IndividualView, etc.).
 * It delegates to the icon-resolver which supports:
 * - MDI icons (mdi-*)
 * - WoB custom SVG icons ($wob-*) registered in Vuetify
 * - Inheritance-based resolution via the Ontology ($wobo)
 *
 * The recIcon() function returns an icon string for use with <v-icon>.
 */

import { resolveIcon, resolveIconMdi } from './icon-resolver'
import { iconRegistry } from './icon-registry'

// Reference to the ontology instance, set via setOntology() during app init.
let _ontology = null

/**
 * Set the ontology instance for inheritance-based icon resolution.
 * Call this once during app initialization, e.g. in vue-wob-cache install.
 *
 * @param {Ontology} ontology - The Ontology instance (typically Vue.prototype.$wobo)
 */
function setOntology(ontology) {
  _ontology = ontology
}

/**
 * Returns an icon string for a REC/WOBO type.
 * Works with <v-icon>{{ recIcon(type) }}</v-icon> for both MDI and $wob-* icons.
 *
 * @param {string|object} type - IRI, short name, or object with .short
 * @param {boolean} [alternative=false] - Use alternative icon (e.g. alarm active state)
 * @returns {string} Icon string (mdi-* or $wob-*)
 */
function recIcon(type, alternative = false) {
  return resolveIconMdi(type, { ontology: _ontology, alternative })
}

/**
 * Returns the full icon resolution result for a type.
 *
 * @param {string|object} type - IRI, short name, or object with .short
 * @param {boolean} [alternative=false] - Use alternative icon
 * @returns {{ mdi: string }} Icon descriptor
 */
function recIconResolved(type, alternative = false) {
  return resolveIcon(type, { ontology: _ontology, alternative })
}

const recIcons = iconRegistry
const defaultRecIcon = 'mdi-asterisk'

export { recIcons, defaultRecIcon, recIcon, recIconResolved, setOntology }
